import { polygonCentroid } from './geo'
import { printBaseMap } from './mapPrintPayload'
import type { PrintProbe } from './printProbe'
import type { GeoPt, TeamMap } from './types'

/**
 * Where a map's ground sits — the point a print sheet is framed around, and
 * the thing a Print button is offered on the existence of.
 *
 * Every parcel, not just the first: a ground whose first stored parcel is
 * degenerate must not fall through to "no centre" for a ground that is
 * perfectly printable otherwise. Falls back to the first pin only when there
 * are no boundary parcels at all (a ground the owner has only pinned, not yet
 * drawn) — `null` means neither exists, so there is genuinely nothing to
 * frame a print around.
 */
export function groundCenterForPrint(map: Pick<TeamMap, 'boundaryAreas' | 'objects'>): GeoPt | null {
  const pts = (map.boundaryAreas ?? []).flatMap((a) => a.rings.filter((r) => r.length >= 3).flat())
  const centre = polygonCentroid(pts)
  if (centre) return centre
  const pin = map.objects?.[0]
  return pin ? { lat: pin.lat, lon: pin.lon } : null
}

/**
 * Whether the base map the surface is drawing is one the print server will not
 * print at the sheet's centre — in which case Create has to be withheld,
 * because the job would be accepted and then fail.
 *
 * The answer is the probe's own list of flavours, so this is no longer a
 * statement about the server kept in step by hand: it used to say "orto on the
 * Mercator fallback", which was true and would have stayed silently true the
 * day the server changed its mind.
 *
 * Before the probe has answered, and where the server prints nothing at all,
 * this blocks nothing: the first is already held by the pending projector, and
 * the second is stated by the panel itself. The alternative to withholding —
 * printing a flavour the user did not choose — is refused everywhere else in
 * this codebase (see `buildMapPrintRequest`'s `baseMapId`), and a sheet that
 * silently arrives as a terrain map when the hunter composed an aerial one is
 * exactly that failure.
 */
export function baseMapBlocksPrint(
  probe: PrintProbe | null | undefined,
  baseMapId: string | null | undefined,
): boolean {
  if (probe?.status !== 'SUCCESS') return false
  return !probe.flavors.includes(printBaseMap(baseMapId))
}

/**
 * The catalogue id of the property-borders overlay — the same id mobile's
 * property-borders switch resolves to (`propertyBordersOverlayId` in
 * `map_layers`).
 */
export const PROPERTY_BORDERS_OVERLAY_ID = 'world-property'

/**
 * Whether the print request asks for property borders: the sheet follows the
 * preview, so it does whenever the property-borders overlay is on there — and
 * the probe says the print server draws them at the sheet's centre.
 *
 * The probe gate is not optional: the server rejects `property_borders: true`
 * where the probe did not offer it, so asking anyway would turn a sheet that
 * prints perfectly well without them into a failed job. Before the probe has
 * answered this is false, like everywhere else the probe has not spoken.
 */
export function printPropertyBorders(
  probe: PrintProbe | null | undefined,
  enabledOverlayIds: ReadonlySet<string>,
): boolean {
  if (probe?.status !== 'SUCCESS') return false
  return probe.propertyBorders && enabledOverlayIds.has(PROPERTY_BORDERS_OVERLAY_ID)
}
